/* eslint-disable react/no-unknown-property */
/* ============================================================
   Monuments – landmark buildings on the section islands
   EiffelTower      : true lattice (merged bars, curved legs,
                      base arches, 3 platforms, spire) + tricolore
   HongKongTower    : Bank-of-China style glass prisms with the
                      iconic X-bracing, granite podium, PRC + HK flags
   StatueOfLiberty  : star fort, granite pedestal, lathe robe with
                      folds, crown rays, torch, tablet + US flag
   All heavy geometry is built once at module level.
   ============================================================ */
import { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { barBetween, mergeAll } from "@/components/world/models/geometryUtils";
import { getFacadeTexture, getStoneTexture } from "@/components/world/textures";
import { Flagpole } from "@/components/world/models/Flag";

/* ============================================================
   EIFFEL TOWER
   ============================================================ */
const EIFFEL_KEYS = [
  [0, 3.4],
  [2.0, 2.92],
  [4.0, 2.45],
  [5.7, 1.98],
  [8.0, 1.62],
  [11.5, 1.18],
  [14.0, 0.98],
  [17.0, 0.8],
  [20.0, 0.64],
  [23.0, 0.52],
  [26.0, 0.42],
  [27.6, 0.36],
];
const eiffelWidth = (h) => {
  for (let i = 0; i < EIFFEL_KEYS.length - 1; i++) {
    const [h0, w0] = EIFFEL_KEYS[i];
    const [h1, w1] = EIFFEL_KEYS[i + 1];
    if (h <= h1) return w0 + ((h - h0) / (h1 - h0)) * (w1 - w0);
  }
  return EIFFEL_KEYS[EIFFEL_KEYS.length - 1][1];
};
const EIFFEL_GEO = (() => {
  const geos = [];
  const corners = [
    [1, 1],
    [-1, 1],
    [-1, -1],
    [1, -1],
  ];
  for (let i = 0; i < EIFFEL_KEYS.length - 1; i++) {
    const [h0, w0] = EIFFEL_KEYS[i];
    const [h1, w1] = EIFFEL_KEYS[i + 1];
    const thick = 0.4 - (h0 / 27.6) * 0.28;
    corners.forEach(([sx, sz]) => geos.push(barBetween([sx * w0, h0, sz * w0], [sx * w1, h1, sz * w1], thick)));
    for (let c = 0; c < 4; c++) {
      const [ax, az] = corners[c];
      const [bx, bz] = corners[(c + 1) % 4];
      geos.push(barBetween([ax * w1, h1, az * w1], [bx * w1, h1, bz * w1], thick * 0.5));
      geos.push(barBetween([ax * w0, h0, az * w0], [bx * w1, h1, bz * w1], thick * 0.3));
      geos.push(barBetween([bx * w0, h0, bz * w0], [ax * w1, h1, az * w1], thick * 0.3));
      // secondary lattice: mid-height horizontal on lower sections
      if (h0 < 12) {
        const hm = (h0 + h1) / 2;
        const wm = eiffelWidth(hm);
        geos.push(barBetween([ax * wm, hm, az * wm], [bx * wm, hm, bz * wm], thick * 0.25));
      }
    }
  }
  // base arches (one per face)
  for (let c = 0; c < 4; c++) {
    const [ax, az] = corners[c];
    const [bx, bz] = corners[(c + 1) % 4];
    const nx = (ax + bx) / 2;
    const nz = (az + bz) / 2; // outward normal (unit, axis aligned)
    const tx = (bx - ax) / 2;
    const tz = (bz - az) / 2; // tangent (unit)
    const pts = [];
    for (let k = 0; k <= 10; k++) {
      const ang = (k / 10) * Math.PI;
      const s = Math.cos(ang) * 0.9;
      const h = 1.4 + Math.sin(ang) * 3.1;
      const w = eiffelWidth(h);
      pts.push([nx * w + tx * s * w, h, nz * w + tz * s * w]);
    }
    for (let k = 0; k < pts.length - 1; k++) geos.push(barBetween(pts[k], pts[k + 1], 0.2));
    // arch infill lattice (vertical ties to the first platform)
    for (let k = 1; k < pts.length - 1; k += 2) {
      const p = pts[k];
      const sw = (tx * p[0] + tz * p[2]) / eiffelWidth(p[1]); // tangent param of the arch point
      const w5 = eiffelWidth(5.7);
      geos.push(barBetween(p, [nx * w5 + tx * sw * w5, 5.7, nz * w5 + tz * sw * w5], 0.06));
    }
  }
  return mergeAll(geos);
})();
const EIFFEL_MAT = new THREE.MeshStandardMaterial({ color: "#5B4636", metalness: 0.55, roughness: 0.55 });

function Platform({ y, half, thick = 0.35, rail = true }) {
  return (
    <group position={[0, y, 0]}>
      <mesh>
        <boxGeometry args={[half * 2, thick, half * 2]} />
        <meshStandardMaterial color="#4A3A2E" metalness={0.5} roughness={0.55} />
      </mesh>
      {rail && (
        <mesh position={[0, thick / 2 + 0.25, 0]}>
          <boxGeometry args={[half * 2 + 0.1, 0.5, half * 2 + 0.1]} />
          <meshStandardMaterial color="#5B4636" metalness={0.5} roughness={0.5} transparent opacity={0.45} />
        </mesh>
      )}
    </group>
  );
}

export function EiffelTower() {
  const lightsRef = useRef([]);
  useFrame((state) => {
    const t = state.clock.elapsedTime;
    lightsRef.current.forEach((m, i) => {
      if (m) m.material.opacity = 0.35 + Math.abs(Math.sin(t * 6 + i * 1.7)) * 0.65;
    });
  });
  return (
    <group position={[12, 1.7, 10]}>
      <mesh geometry={EIFFEL_GEO} material={EIFFEL_MAT} />
      <Platform y={5.7} half={2.3} />
      <Platform y={11.5} half={1.45} thick={0.3} />
      <Platform y={27.6} half={0.75} thick={0.25} rail={false} />
      {/* observation cabin + spire */}
      <mesh position={[0, 28.3, 0]}>
        <boxGeometry args={[1.0, 1.1, 1.0]} />
        <meshStandardMaterial color="#4A3A2E" metalness={0.55} roughness={0.5} />
      </mesh>
      <mesh position={[0, 30.2, 0]}>
        <cylinderGeometry args={[0.04, 0.22, 3.0, 8]} />
        <meshStandardMaterial color="#5B4636" metalness={0.6} roughness={0.5} />
      </mesh>
      <mesh position={[0, 31.8, 0]}>
        <sphereGeometry args={[0.16, 10, 8]} />
        <meshBasicMaterial color="#FFD27A" toneMapped={false} />
      </mesh>
      {/* sparkle lights on the lattice */}
      {[
        [2.1, 3.0, 2.1],
        [-2.1, 4.2, 2.1],
        [1.6, 8.5, -1.6],
        [-1.2, 13.5, -1.2],
        [0.9, 18.0, 0.9],
        [-0.6, 22.5, 0.6],
        [2.3, 5.9, -2.3],
        [-2.3, 5.9, -2.3],
      ].map((p, i) => (
        <mesh key={i} ref={(el) => (lightsRef.current[i] = el)} position={p}>
          <sphereGeometry args={[0.1, 6, 6]} />
          <meshBasicMaterial color="#FFE7B0" transparent opacity={0.8} toneMapped={false} />
        </mesh>
      ))}
      {/* tricolore at the summit */}
      <Flagpole kind="france" height={2.2} flagWidth={1.5} flagHeight={1.0} position={[0.3, 30.4, 0]} phase={1.2} />
      {/* ground flagpoles at the esplanade */}
      <Flagpole kind="france" height={6} flagWidth={2.1} flagHeight={1.4} position={[5.5, 0, 5.5]} phase={0.3} />
      <pointLight color="#FFD27A" intensity={1.3} distance={35} position={[0, 25, 0]} />
    </group>
  );
}

/* ============================================================
   BANK OF CHINA TOWER (Hong Kong)
   ============================================================ */
const BOC_HALF = 3;
const BOC_QUADS = [
  // [cornerA, cornerB] in (x, zWorld) – outer edge, plus height
  { a: [BOC_HALF, BOC_HALF], b: [-BOC_HALF, BOC_HALF], h: 13 }, // front (+z)
  { a: [BOC_HALF, -BOC_HALF], b: [BOC_HALF, BOC_HALF], h: 19 }, // right (+x)
  { a: [-BOC_HALF, BOC_HALF], b: [-BOC_HALF, -BOC_HALF], h: 25 }, // left (-x)
  { a: [-BOC_HALF, -BOC_HALF], b: [BOC_HALF, -BOC_HALF], h: 31 }, // back (-z)
];
const BOC_PRISMS = BOC_QUADS.map((q) => {
  const s = new THREE.Shape();
  // shape (x, y) → world (x, ·, -y)
  s.moveTo(0, 0);
  s.lineTo(q.a[0], -q.a[1]);
  s.lineTo(q.b[0], -q.b[1]);
  s.lineTo(0, 0);
  const g = new THREE.ExtrudeGeometry(s, { depth: q.h, bevelEnabled: false });
  g.rotateX(-Math.PI / 2);
  g.computeVertexNormals();
  return g;
});
const BOC_BRACE_GEO = (() => {
  const geos = [];
  const MODULE = 6;
  const braceFace = (A, B, y0, y1) => {
    const mx = (A[0] + B[0]) / 2;
    const mz = (A[1] + B[1]) / 2;
    const nl = Math.hypot(mx, mz) || 1;
    const nx = (mx / nl) * 0.1;
    const nz = (mz / nl) * 0.1;
    const P = (c, y) => [c[0] + nx, y, c[1] + nz];
    for (let y = 0; y < y1; y += MODULE) {
      const top = Math.min(y + MODULE, y1);
      if (top <= y0) continue;
      const base = Math.max(y, y0);
      geos.push(barBetween(P(A, base), P(B, top), 0.14));
      geos.push(barBetween(P(B, base), P(A, top), 0.14));
      geos.push(barBetween(P(A, top), P(B, top), 0.14));
      if (base === y0) geos.push(barBetween(P(A, base), P(B, base), 0.14));
    }
    // vertical corner frames
    geos.push(barBetween(P(A, y0), P(A, y1), 0.16));
    geos.push(barBetween(P(B, y0), P(B, y1), 0.16));
  };
  BOC_QUADS.forEach((q) => braceFace(q.a, q.b, 0, q.h));
  // exposed inner (diagonal) faces above the neighbouring quadrant
  const centre = [0, 0];
  BOC_QUADS.forEach((q) => {
    [q.a, q.b].forEach((corner) => {
      const neighbour = BOC_QUADS.filter((o) => o !== q && (sameCorner(o.a, corner) || sameCorner(o.b, corner)))[0];
      if (neighbour && neighbour.h < q.h) braceFace(centre, corner, neighbour.h, q.h);
    });
  });
  return mergeAll(geos);
  function sameCorner(a, b) {
    return a[0] === b[0] && a[1] === b[1];
  }
})();

export function HongKongTower() {
  const beaconRef = useRef();
  const facade = useMemo(() => {
    const t = getFacadeTexture("#4C6E8C", 0.1, 5);
    if (t) t.repeat.set(1 / 3, 1 / 6);
    return t;
  }, []);
  const glassMat = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        map: facade || null,
        color: facade ? "#FFFFFF" : "#2A4A6A",
        metalness: 0.6,
        roughness: 0.2,
        emissive: "#2A4560",
        emissiveIntensity: 0.22,
      }),
    [facade]
  );
  const capMat = useMemo(() => new THREE.MeshStandardMaterial({ color: "#4A5560", metalness: 0.7, roughness: 0.4 }), []);
  const stone = useMemo(() => {
    const t = getStoneTexture("#6E6A66", 11, 26);
    if (t) t.repeat.set(2, 1);
    return t;
  }, []);
  useFrame((state) => {
    if (beaconRef.current) {
      const t = state.clock.elapsedTime;
      beaconRef.current.material.emissiveIntensity = (t * 1.1) % 1 < 0.15 ? 3.2 : 0.4;
    }
  });
  return (
    <group position={[12, 1.7, 10]}>
      {/* granite podium */}
      <mesh position={[0, 0.9, 0]}>
        <boxGeometry args={[8.4, 1.8, 8.4]} />
        <meshStandardMaterial map={stone || null} color={stone ? "#FFFFFF" : "#6E6A66"} roughness={0.85} />
      </mesh>
      <mesh position={[0, 0.2, 0]}>
        <boxGeometry args={[9.6, 0.4, 9.6]} />
        <meshStandardMaterial color="#5A5652" roughness={0.9} />
      </mesh>
      {/* entrance canopy */}
      <mesh position={[0, 1.95, 4.9]}>
        <boxGeometry args={[4.2, 0.12, 1.6]} />
        <meshStandardMaterial color="#C8CCD2" metalness={0.9} roughness={0.3} />
      </mesh>
      <mesh position={[0, 1.2, 4.22]}>
        <boxGeometry args={[3.0, 1.8, 0.1]} />
        <meshStandardMaterial color="#0F1A26" metalness={0.9} roughness={0.1} emissive="#FFE7B0" emissiveIntensity={0.25} />
      </mesh>
      {/* glass prisms */}
      <group position={[0, 1.8, 0]}>
        {BOC_PRISMS.map((g, i) => (
          <mesh key={i} geometry={g} material={[capMat, glassMat]} />
        ))}
        <mesh geometry={BOC_BRACE_GEO}>
          <meshStandardMaterial color="#E8EAEE" metalness={0.8} roughness={0.3} />
        </mesh>
        {/* mechanical penthouse + twin masts on the tallest prism */}
        <mesh position={[-1.2, 31.5, -1.2]}>
          <boxGeometry args={[1.6, 1.0, 1.6]} />
          <meshStandardMaterial color="#4A5560" metalness={0.7} roughness={0.4} />
        </mesh>
        {[-0.5, 0.5].map((ox, i) => (
          <mesh key={i} position={[-1.2 + ox, 35.5, -1.2]}>
            <cylinderGeometry args={[0.05, 0.12, 7, 8]} />
            <meshStandardMaterial color="#D9D6D0" metalness={0.9} roughness={0.2} />
          </mesh>
        ))}
        <mesh ref={beaconRef} position={[-1.2, 39.2, -1.2]}>
          <sphereGeometry args={[0.28, 10, 10]} />
          <meshStandardMaterial color="#E94B4B" emissive="#E94B4B" emissiveIntensity={1.5} toneMapped={false} />
        </mesh>
      </group>
      {/* PRC + HKSAR flags in front of the podium */}
      <Flagpole kind="china" height={7} flagWidth={2.1} flagHeight={1.4} position={[-3.2, 0.4, 6.2]} phase={0.4} />
      <Flagpole kind="hongkong" height={7} flagWidth={2.1} flagHeight={1.4} position={[1.6, 0.4, 6.2]} phase={1.7} />
      <pointLight color="#8AC5FF" intensity={1.4} distance={45} position={[0, 20, 6]} />
    </group>
  );
}

/* ============================================================
   STATUE OF LIBERTY
   ============================================================ */
const ROBE_GEO = (() => {
  const profile = [
    [1.95, 0],
    [1.9, 0.5],
    [1.68, 1.6],
    [1.42, 3.0],
    [1.22, 4.2],
    [1.1, 5.0],
    [1.0, 5.6],
    [0.78, 6.2],
    [0.001, 6.35],
  ].map(([r, y]) => new THREE.Vector2(r, y));
  const g = new THREE.LatheGeometry(profile, 56);
  const pos = g.attributes.position;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const y = pos.getY(i);
    const z = pos.getZ(i);
    const r = Math.hypot(x, z);
    if (r < 0.01) continue;
    const phi = Math.atan2(x, z);
    const fold = 1 + (0.055 * Math.sin(phi * 9 + y * 0.6) + 0.025 * Math.sin(phi * 21)) * (1 - (y / 6.35) * 0.55);
    pos.setX(i, x * fold);
    pos.setZ(i, z * fold);
  }
  g.computeVertexNormals();
  return g;
})();

export function StatueOfLiberty() {
  const flameRef = useRef();
  const patina = useMemo(() => {
    const t = getStoneTexture("#6FAE9A", 8, 34);
    if (t) t.repeat.set(3, 3);
    return t;
  }, []);
  const patinaMat = useMemo(
    () => new THREE.MeshStandardMaterial({ map: patina || null, color: patina ? "#FFFFFF" : "#6FAE9A", roughness: 0.72, metalness: 0.18 }),
    [patina]
  );
  const granite = useMemo(() => {
    const t = getStoneTexture("#9A8E80", 13, 30);
    if (t) t.repeat.set(2, 2);
    return t;
  }, []);
  const graniteMat = useMemo(
    () => new THREE.MeshStandardMaterial({ map: granite || null, color: granite ? "#FFFFFF" : "#9A8E80", roughness: 0.9 }),
    [granite]
  );
  const gold = useMemo(() => new THREE.MeshStandardMaterial({ color: "#D9A83C", metalness: 0.95, roughness: 0.25 }), []);
  const rays = useMemo(() => {
    const arr = [];
    const up = new THREE.Vector3(0, 1, 0);
    for (let i = 0; i < 7; i++) {
      const a = -Math.PI / 2 + (i / 6) * Math.PI;
      const d = new THREE.Vector3(Math.sin(a) * 0.95, 0.55, Math.cos(a) * 0.95).normalize();
      const q = new THREE.Quaternion().setFromUnitVectors(up, d);
      arr.push({ pos: d.clone().multiplyScalar(1.05), q });
    }
    return arr;
  }, []);

  useFrame((state) => {
    if (flameRef.current) {
      const t = state.clock.elapsedTime;
      flameRef.current.scale.set(1 + Math.sin(t * 9) * 0.08, 1 + Math.sin(t * 8) * 0.18, 1 + Math.cos(t * 7) * 0.08);
      flameRef.current.material.emissiveIntensity = 1.3 + Math.sin(t * 6) * 0.4;
    }
  });

  const armDir = new THREE.Vector3(0.42, 1, 0).normalize();
  const armQ = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), armDir);
  const lArmDir = new THREE.Vector3(-0.3, -0.55, 0.78).normalize();
  const lArmQ = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), lArmDir);

  return (
    <group position={[12, 1.7, 10]}>
      {/* star fort */}
      <mesh position={[0, 0.6, 0]} material={graniteMat}>
        <cylinderGeometry args={[5.2, 5.6, 1.2, 11]} />
      </mesh>
      <mesh position={[0, 1.65, 0]} material={graniteMat}>
        <cylinderGeometry args={[4.1, 4.5, 0.9, 11]} />
      </mesh>
      {/* pedestal */}
      <mesh position={[0, 3.7, 0]} material={graniteMat}>
        <boxGeometry args={[3.9, 3.2, 3.9]} />
      </mesh>
      <mesh position={[0, 5.4, 0]}>
        <boxGeometry args={[4.3, 0.3, 4.3]} />
        <meshStandardMaterial color="#B3A797" roughness={0.85} />
      </mesh>
      <mesh position={[0, 7.0, 0]} material={graniteMat}>
        <boxGeometry args={[3.3, 2.9, 3.3]} />
      </mesh>
      <mesh position={[0, 8.55, 0]}>
        <boxGeometry args={[3.7, 0.25, 3.7]} />
        <meshStandardMaterial color="#B3A797" roughness={0.85} />
      </mesh>
      {/* pilasters + window slits on the 4 faces */}
      {[0, 1, 2, 3].map((f) => (
        <group key={f} rotation={[0, (f * Math.PI) / 2, 0]}>
          {[-1.1, 1.1].map((ox) => (
            <mesh key={ox} position={[ox, 7.0, 1.68]}>
              <boxGeometry args={[0.3, 2.9, 0.12]} />
              <meshStandardMaterial color="#B3A797" roughness={0.85} />
            </mesh>
          ))}
          {[-0.45, 0.45].map((ox) => (
            <mesh key={`w${ox}`} position={[ox, 7.3, 1.67]}>
              <boxGeometry args={[0.18, 0.7, 0.06]} />
              <meshStandardMaterial color="#1E1A18" roughness={0.9} />
            </mesh>
          ))}
          <mesh position={[0, 2.75, 1.98]}>
            <boxGeometry args={[0.6, 1.1, 0.08]} />
            <meshStandardMaterial color="#2A2420" roughness={0.9} />
          </mesh>
        </group>
      ))}
      {/* ---- statue ---- */}
      <group position={[0, 8.68, 0]} scale={1.15}>
        <mesh geometry={ROBE_GEO} material={patinaMat} />
        {/* chest & shoulders */}
        <mesh position={[0, 6.15, 0]} material={patinaMat} scale={[1.25, 0.72, 0.9]}>
          <sphereGeometry args={[1.0, 20, 14]} />
        </mesh>
        <mesh position={[0, 6.85, 0]} material={patinaMat}>
          <cylinderGeometry args={[0.3, 0.36, 0.55, 12]} />
        </mesh>
        {/* head */}
        <group position={[0, 7.6, 0]}>
          <mesh material={patinaMat} scale={[0.92, 1.05, 0.96]}>
            <sphereGeometry args={[0.62, 22, 16]} />
          </mesh>
          {/* hair mass at the back */}
          <mesh material={patinaMat} position={[0, -0.05, -0.2]} scale={[1.0, 1.0, 0.9]}>
            <sphereGeometry args={[0.6, 16, 12, 0, Math.PI * 2, 0, Math.PI * 0.62]} />
          </mesh>
          {/* nose + brow */}
          <mesh material={patinaMat} position={[0, -0.05, 0.6]} rotation={[0.3, 0, 0]}>
            <coneGeometry args={[0.09, 0.3, 8]} />
          </mesh>
          <mesh material={patinaMat} position={[0, 0.16, 0.5]}>
            <boxGeometry args={[0.55, 0.08, 0.2]} />
          </mesh>
          {/* crown band + rays */}
          <mesh material={patinaMat} position={[0, 0.5, 0]} rotation={[Math.PI / 2, 0, 0]}>
            <torusGeometry args={[0.62, 0.09, 8, 28]} />
          </mesh>
          {rays.map((r, i) => (
            <mesh key={i} position={[r.pos.x, 0.5 + r.pos.y, r.pos.z]} quaternion={r.q} material={patinaMat}>
              <coneGeometry args={[0.1, 1.5, 6]} />
            </mesh>
          ))}
        </group>
        {/* raised right arm with torch */}
        <group position={[0.95, 6.1, 0]}>
          <mesh quaternion={armQ} position={[armDir.x * 0.9, armDir.y * 0.9, 0]} material={patinaMat}>
            <cylinderGeometry args={[0.28, 0.34, 1.9, 12]} />
          </mesh>
          <group position={[armDir.x * 1.85, armDir.y * 1.85, 0]}>
            <mesh position={[0, 0.9, 0]} material={patinaMat}>
              <cylinderGeometry args={[0.24, 0.28, 1.8, 12]} />
            </mesh>
            <mesh position={[0, 1.85, 0]} material={patinaMat}>
              <sphereGeometry args={[0.36, 12, 10]} />
            </mesh>
            {/* torch */}
            <mesh position={[0, 2.5, 0]} material={gold}>
              <cylinderGeometry args={[0.1, 0.16, 1.2, 10]} />
            </mesh>
            <mesh position={[0, 3.2, 0]} material={gold}>
              <cylinderGeometry args={[0.42, 0.24, 0.5, 12]} />
            </mesh>
            <mesh ref={flameRef} position={[0, 3.95, 0]}>
              <coneGeometry args={[0.4, 1.3, 10]} />
              <meshStandardMaterial color="#FFB347" emissive="#FFA020" emissiveIntensity={1.4} toneMapped={false} transparent opacity={0.95} />
            </mesh>
            <mesh position={[0, 4.6, 0]}>
              <sphereGeometry args={[0.22, 10, 8]} />
              <meshBasicMaterial color="#FFF0C0" toneMapped={false} />
            </mesh>
            <pointLight color="#FFB347" intensity={1.6} distance={26} position={[0, 4.0, 0]} />
          </group>
        </group>
        {/* left arm + tablet */}
        <group position={[-0.95, 6.1, 0]}>
          <mesh quaternion={lArmQ} position={[lArmDir.x * 0.85, lArmDir.y * 0.85, lArmDir.z * 0.85]} material={patinaMat}>
            <cylinderGeometry args={[0.26, 0.32, 1.7, 12]} />
          </mesh>
          <group position={[lArmDir.x * 1.7, lArmDir.y * 1.7, lArmDir.z * 1.7]}>
            <mesh rotation={[0.35, 0.15, 0.25]} material={patinaMat}>
              <boxGeometry args={[1.25, 1.7, 0.22]} />
            </mesh>
            {[0.5, 0.2, -0.1, -0.4].map((yo, i) => (
              <mesh key={i} position={[0, yo, 0.13]} rotation={[0.35, 0.15, 0.25]}>
                <boxGeometry args={[0.8, 0.07, 0.02]} />
                <meshStandardMaterial color="#3A5850" roughness={0.9} />
              </mesh>
            ))}
          </group>
        </group>
        {/* broken chain at the feet */}
        {[0, 1, 2].map((i) => (
          <mesh key={i} position={[0.7 + i * 0.32, 0.12, 1.55 - i * 0.1]} rotation={[Math.PI / 2, 0, i * 0.5]} material={patinaMat}>
            <torusGeometry args={[0.14, 0.04, 6, 12]} />
          </mesh>
        ))}
      </group>
      {/* US flag on the fort */}
      <Flagpole kind="usa" height={9} flagWidth={2.6} flagHeight={1.37} position={[3.6, 2.1, 3.4]} phase={2.1} />
      <pointLight color="#F0A020" intensity={0.8} distance={30} position={[0, 12, 6]} />
    </group>
  );
}
